import type { EditorTable } from './context';
import {
    clamp,
    Point,
    SizeMeters,
    snapMeters,
    TABLE_SIZE_MAX,
    TABLE_SIZE_MIN,
    tableFootprintMeters,
    TableShape,
    TableShapeSizes,
} from './mapGeometry';

export const TABLE_SHAPE_NAME_KEY: Record<TableShape, string> = {
    round: 'tableMaps.wizard.tables.shapeRound',
    square: 'tableMaps.wizard.tables.shapeSquare',
    rectangle: 'tableMaps.wizard.tables.shapeRectangle',
};

/** TablesMapManager.ValidateMapTableSeats (MapTableSeatsOutOfRange). */
export const TABLE_SEATS_MIN = 1;
export const TABLE_SEATS_MAX = 30;

/** TablesMapManager.ApplyMapTableNameRules (MapTableNameTooLong), counted after trimming. */
export const TABLE_NAME_MAX_LENGTH = 50;

export function nextTableLabel(tables: EditorTable[]): string {
    const taken = new Set(tables.map((t) => t.label.trim().toLowerCase()));
    let n = 1;
    while (taken.has(`t${n}`)) n += 1;
    return `T${n}`;
}

const TABLE_FIELDS: (keyof EditorTable)[] = [
    'label',
    'shape',
    'seats',
    'qrCode',
    'x',
    'y',
    'width',
    'height',
    'rotation',
    'isActive',
    'isPositionLocked',
];

export function tableChanged(current: EditorTable, original: EditorTable): boolean {
    return TABLE_FIELDS.some((key) => current[key] !== original[key]);
}

export function lastEditedTableId(
    previous: EditorTable[] | undefined,
    current: EditorTable[]
): string | null {
    if (!previous) return null;
    const before = new Map(previous.map((t) => [t.id, t]));
    const edited = current.find((t) => {
        const original = before.get(t.id);
        return !original || tableChanged(t, original);
    });
    return edited?.id ?? null;
}

export type TableEdit = Pick<
    EditorTable,
    | 'label'
    | 'shape'
    | 'seats'
    | 'qrCode'
    | 'width'
    | 'height'
    | 'rotation'
    | 'isActive'
    | 'isPositionLocked'
>;

/** The step-5 fields as shown: numbers stay text, so "1." or an empty field survives typing. */
export type TableInputs = Pick<
    EditorTable,
    'label' | 'shape' | 'qrCode' | 'isActive' | 'isPositionLocked'
> & {
    seats: string;
    width: string;
    height: string;
    rotation: string;
};

export interface PendingTableEdit {
    id: string;
    inputs: TableInputs;
    edit: TableEdit;
}

export function tableEditOf(table: EditorTable): TableEdit {
    const { label, shape, seats, qrCode, width, height, rotation, isActive, isPositionLocked } =
        table;
    return { label, shape, seats, qrCode, width, height, rotation, isActive, isPositionLocked };
}

/** A legacy size of 0 shows as an empty field, which the helper text says means the shape default. */
export function tableInputsOf(table: EditorTable): TableInputs {
    return {
        label: table.label,
        shape: table.shape,
        qrCode: table.qrCode,
        isActive: table.isActive,
        isPositionLocked: table.isPositionLocked,
        seats: String(table.seats),
        width: table.width ? String(table.width) : '',
        height: table.height ? String(table.height) : '',
        rotation: String(table.rotation),
    };
}

export type TableSizeError = 'notPositive' | 'tooSmall' | 'tooLarge' | 'largerThanMap';

export type TableSizeInput =
    | { status: 'empty' }
    | { status: 'partial' }
    | { status: 'valid'; value: number }
    | { status: 'invalid'; error: TableSizeError };

const DECIMAL = /^-?(\d+([.,]\d+)?|[.,]\d+)$/;
const PARTIAL_DECIMAL = /^-?\d*[.,]?$/;

function tableSizeError(value: number, mapAxis: number): TableSizeError | null {
    if (value <= 0) return 'notPositive';
    if (value < TABLE_SIZE_MIN) return 'tooSmall';
    if (value > TABLE_SIZE_MAX) return 'tooLarge';
    if (value > mapAxis) return 'largerThanMap';
    return null;
}

export function parseTableSize(raw: string, mapAxis: number): TableSizeInput {
    const text = raw.trim();
    if (text === '') return { status: 'empty' };
    if (!DECIMAL.test(text)) {
        return PARTIAL_DECIMAL.test(text)
            ? { status: 'partial' }
            : { status: 'invalid', error: 'notPositive' };
    }
    const value = Number(text.replace(',', '.'));
    const error = tableSizeError(value, mapAxis);
    return error ? { status: 'invalid', error } : { status: 'valid', value: snapMeters(value) };
}

export function parseSeats(raw: string): number | null {
    const text = raw.trim();
    if (!/^\d+$/.test(text)) return null;
    const seats = Number(text);
    return seats >= TABLE_SEATS_MIN && seats <= TABLE_SEATS_MAX ? seats : null;
}

export function stepSeats(raw: string, fallback: number, delta: 1 | -1): number {
    return clamp((parseSeats(raw) ?? fallback) + delta, TABLE_SEATS_MIN, TABLE_SEATS_MAX);
}

export function parseRotation(raw: string): number | null {
    const text = raw.trim();
    if (!DECIMAL.test(text)) return null;
    const degrees = Number(text.replace(',', '.'));
    return degrees >= 0 && degrees < 360 ? Math.round(degrees) % 360 : null;
}

function sizeOrHeld(raw: string, mapAxis: number, shapeDefault: number, held: number): number {
    const parsed = parseTableSize(raw, mapAxis);
    if (parsed.status === 'empty') return shapeDefault;
    return parsed.status === 'valid' ? parsed.value : held;
}

/** A table without a QR code is always inactive: clearing it switches off, a first code switches on. */
export function applyTableInputs(
    pending: PendingTableEdit,
    patch: Partial<TableInputs>,
    tableDefaults: TableShapeSizes,
    mapSize: SizeMeters
): PendingTableEdit {
    const typed = { ...pending.inputs, ...patch };
    const isActive = !!typed.qrCode && (pending.inputs.qrCode ? typed.isActive : true);
    const inputs = { ...typed, isActive };
    const held = pending.edit;
    const shapeDefault = tableDefaults[inputs.shape];
    return {
        id: pending.id,
        inputs,
        edit: {
            label: inputs.label.trim(),
            shape: inputs.shape,
            qrCode: inputs.qrCode,
            isActive: inputs.isActive,
            isPositionLocked: inputs.isPositionLocked,
            seats: parseSeats(inputs.seats) ?? held.seats,
            width: sizeOrHeld(inputs.width, mapSize.width, shapeDefault.width, held.width),
            height: sizeOrHeld(inputs.height, mapSize.height, shapeDefault.height, held.height),
            rotation: parseRotation(inputs.rotation) ?? held.rotation,
        },
    };
}

export interface TableInputErrors {
    label: 'required' | 'tooLong' | 'duplicate' | null;
    seats: boolean;
    width: TableSizeError | null;
    height: TableSizeError | null;
    rotation: boolean;
    qrInUse: boolean;
}

function sizeError(input: TableSizeInput): TableSizeError | null {
    return input.status === 'invalid' ? input.error : null;
}

export function tableInputErrors(
    inputs: TableInputs,
    otherTables: EditorTable[],
    mapSize: SizeMeters
): TableInputErrors {
    const label = inputs.label.trim().toLowerCase();
    const isDuplicate = otherTables.some((t) => t.label.trim().toLowerCase() === label);
    return {
        label:
            label === ''
                ? 'required'
                : label.length > TABLE_NAME_MAX_LENGTH
                ? 'tooLong'
                : isDuplicate
                ? 'duplicate'
                : null,
        seats: parseSeats(inputs.seats) === null,
        width: sizeError(parseTableSize(inputs.width, mapSize.width)),
        height: sizeError(parseTableSize(inputs.height, mapSize.height)),
        rotation: parseRotation(inputs.rotation) === null,
        qrInUse:
            inputs.isActive &&
            !!inputs.qrCode &&
            otherTables.some((t) => t.isActive && t.qrCode === inputs.qrCode),
    };
}

/** Whether the edit draws the table differently: a legacy 0 and its shape default draw the same. */
export function isGeometryEdited(
    table: EditorTable,
    edit: TableEdit,
    tableDefaults: TableShapeSizes
): boolean {
    const before = tableFootprintMeters(table.shape, table.width, table.height, tableDefaults);
    const after = tableFootprintMeters(edit.shape, edit.width, edit.height, tableDefaults);
    return (
        table.shape !== edit.shape ||
        table.rotation !== edit.rotation ||
        before.width !== after.width ||
        before.height !== after.height
    );
}

export function sizeFromCornerDrag(
    offset: Point,
    table: Pick<TableEdit, 'shape' | 'rotation'>,
    pxPerMeter: number,
    mapSize: SizeMeters
): { size: SizeMeters; isAtMax: boolean } {
    const rad = (table.rotation * Math.PI) / 180;
    const localX = offset.x * Math.cos(rad) + offset.y * Math.sin(rad);
    const localY = -offset.x * Math.sin(rad) + offset.y * Math.cos(rad);
    const width = (2 * Math.abs(localX)) / pxPerMeter;
    const height = (2 * Math.abs(localY)) / pxPerMeter;
    const maxWidth = Math.min(TABLE_SIZE_MAX, mapSize.width);
    const maxHeight = Math.min(TABLE_SIZE_MAX, mapSize.height);
    const fit = (value: number, max: number) => snapMeters(clamp(value, TABLE_SIZE_MIN, max));
    if (table.shape === 'round') {
        const diameter = Math.max(width, height);
        const max = Math.min(maxWidth, maxHeight);
        const fitted = fit(diameter, max);
        return { size: { width: fitted, height: fitted }, isAtMax: diameter > max };
    }
    return {
        size: { width: fit(width, maxWidth), height: fit(height, maxHeight) },
        isAtMax: width > maxWidth || height > maxHeight,
    };
}
